import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { StatusBadge } from "@/components/app/ui-bits";
import { CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import SalaryBatchesPanel from "@/components/app/SalaryBatchesPanel";
import { BANK_ACCOUNTS } from "./_shared";
import RecordInspector, { useRecordInspector } from "@/components/app/RecordInspector";

const STATUS_LABELS = {
  submitted: "Awaiting Finance",
  finance_review: "Under Finance Review",
  finance_cleared: "Cleared · Awaiting CEO",
  finance_rejected: "Rejected by Finance",
  admin_review: "Awaiting CEO",
  approved: "Approved · Pending payment",
  rejected: "Rejected by CEO",
  paid: "Paid",
};

export default function PayrollReview({ canEdit, userEmail, staffName }) {
  const inspector = useRecordInspector();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState("");
  const [payOpen, setPayOpen] = useState(null);
  const [view, setView] = useState("individual"); // "individual" | "bulk"

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hr/salary-requests");
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const transition = async (id, status, c) => {
    try {
      await api.post(`/hr/salary-requests/${id}/transition`, { status, comment: c });
      toast.success("Status updated");
      setSelected(null);
      setComment("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const cur = new Date();
  const m = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}`;
  const awaitingFinance = items.filter((x) => ["submitted", "finance_review"].includes(x.status));
  const cleared = items.filter((x) => ["finance_cleared", "admin_review"].includes(x.status));
  const paidThisMonth = items.filter((x) => x.status === "paid" && (x.period_month || "").startsWith(m));
  const paidTotal = paidThisMonth.reduce((acc, x) => acc + (x.net_amount || x.gross_amount || 0), 0);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-display text-lg font-semibold text-slate-900">Payroll Clearance</h2>
        <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="payroll-view-toggle">
          <button
            type="button"
            onClick={() => setView("individual")}
            data-testid="payroll-view-individual"
            className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm transition ${
              view === "individual" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            Individual review
          </button>
          <button
            type="button"
            onClick={() => setView("bulk")}
            data-testid="payroll-view-bulk"
            className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm transition ${
              view === "bulk" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            HR payroll batches
          </button>
        </div>
      </div>

      {view === "bulk" ? (
        <SalaryBatchesPanel
          mode="finance"
          title="HR payroll batches"
          description="Bulk salary batches submitted by HR. Approve, reject or partially clear lines, then forward to CEO for final approval."
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="dc-card p-4">
              <div className="overline">Awaiting Finance</div>
              <div className="mt-1 text-3xl font-display font-semibold text-amber-600 tabular-nums">{awaitingFinance.length}</div>
            </div>
            <div className="dc-card p-4">
              <div className="overline">Cleared to Admin</div>
              <div className="mt-1 text-3xl font-display font-semibold text-blue-700 tabular-nums">{cleared.length}</div>
            </div>
            <div className="dc-card p-4">
              <div className="overline">Paid total ({m})</div>
              <div className="mt-1 text-3xl font-display font-semibold text-emerald-700 tabular-nums">{formatCurrency(paidTotal)}</div>
              <div className="text-xs text-slate-500">{paidThisMonth.length} record(s)</div>
            </div>
          </div>

          <div className="dc-card overflow-hidden">
            <div className="border-b border-slate-100 px-5 py-3">
              <h3 className="font-display text-[15px] font-semibold text-slate-900">Payroll Clearance Queue</h3>
            </div>
            {loading ? (
              <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-500">No salary requests submitted yet.</div>
            ) : (
              <div className="responsive-table-wrap">
                <table className="data-table w-full min-w-[920px]">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Period</th>
                      <th className="text-right">Gross</th>
                      <th className="text-right">Net</th>
                      <th>Status</th>
                      <th className="w-44">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((r) => {
                      const isPayable = r.status === "approved" || r.status === "approved_for_disbursement";
                      return (
                        <tr
                          key={r.id}
                          className="text-sm cursor-pointer hover:bg-slate-50/70 transition-colors"
                          onClick={() => inspector.open(r, {
                            title: `Salary request · ${r.staff_name || staffName(r.staff_id)}`,
                            subtitle: `${r.period_month} · Net ${formatCurrency(r.net_amount)}`,
                          })}
                        >
                          <td className="px-5 py-3 font-semibold text-slate-900" data-testid={`payroll-employee-${r.id}`}>
                            {r.staff_name || staffName(r.staff_id)}
                          </td>
                          <td className="px-5 py-3 font-mono-alt text-xs">{r.period_month}</td>
                          <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{formatCurrency(r.gross_amount)}</td>
                          <td className="px-5 py-3 text-right font-mono-alt tabular-nums font-semibold">{formatCurrency(r.net_amount)}</td>
                          <td className="px-5 py-3">
                            <StatusBadge status={
                              r.status === "submitted" ? "pending"
                              : r.status === "finance_cleared" ? "in_progress"
                              : r.status === "approved" ? "approved"
                              : r.status === "paid" ? "paid"
                              : r.status === "rejected" || r.status === "finance_rejected" ? "rejected"
                              : "pending"
                            } />
                          </td>
                          <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                            {canEdit && ["submitted", "finance_review"].includes(r.status) ? (
                              <Button size="sm" onClick={() => setSelected(r)} data-testid={`payroll-review-${r.id}`}>Review</Button>
                            ) : isPayable ? (
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setPayOpen(r)} data-testid={`payroll-pay-${r.id}`}>
                                <CheckCircle size={12} className="mr-1" />Mark as Paid
                              </Button>
                            ) : (
                              <span className="text-xs text-slate-400">{STATUS_LABELS[r.status] || "—"}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
            <DialogContent className="max-w-lg">
              {selected && (
                <>
                  <DialogHeader><DialogTitle>Payroll review — {selected.period_month}</DialogTitle></DialogHeader>
                  <div className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-3">
                      <div><div className="overline">Employee</div><div className="font-medium">{selected.staff_name || staffName(selected.staff_id)}</div></div>
                      <div><div className="overline">Period</div><div className="font-medium">{selected.period_month}</div></div>
                      <div><div className="overline">Gross</div><div className="font-medium">{formatCurrency(selected.gross_amount)}</div></div>
                      <div><div className="overline">Net</div><div className="font-semibold text-slate-900">{formatCurrency(selected.net_amount)}</div></div>
                    </div>
                    <div>
                      <Label>Comment (required)</Label>
                      <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Explain the decision…" />
                    </div>
                  </div>
                  <DialogFooter className="gap-2">
                    <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={!comment.trim()} onClick={() => transition(selected.id, "finance_cleared", comment)}>Clear to CEO</Button>
                    <Button variant="destructive" disabled={!comment.trim()} onClick={() => transition(selected.id, "finance_rejected", comment)}>Reject</Button>
                    <Button variant="ghost" onClick={() => setSelected(null)}>Cancel</Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>

          <PayDialog
            open={!!payOpen}
            onOpenChange={(o) => setPayOpen(o ? payOpen : null)}
            item={payOpen}
            userEmail={userEmail}
            onDone={load}
            staffName={staffName}
          />
        </>
      )}
      <RecordInspector {...inspector.props} />
    </div>
  );
}

function PayDialog({ open, onOpenChange, item, userEmail, onDone, staffName }) {
  const [form, setForm] = useState({
    payment_date: new Date().toISOString().slice(0, 10),
    bank_account: BANK_ACCOUNTS[0],
    reference: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        payment_date: new Date().toISOString().slice(0, 10),
        bank_account: BANK_ACCOUNTS[0],
        reference: "",
        notes: "",
      });
    }
  }, [open]);

  if (!item) return null;

  const lastApproverEmail = (item.workflow || []).slice().reverse()
    .find((w) => ["approved", "approved_for_disbursement"].includes(w.to))?.actor;
  const conflict = lastApproverEmail && lastApproverEmail === userEmail;

  const submit = async () => {
    if (!form.reference.trim()) return toast.error("Reference number is required");
    setBusy(true);
    try {
      await api.post(`/finance/payroll/${item.id}/mark-paid`, form);
      toast.success("Marked as paid");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="payroll-pay-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Mark payroll as paid</DialogTitle>
          <DialogDescription>
            {(item.staff_name || staffName(item.staff_id))} · {item.period_month} · {formatCurrency(item.net_amount)}
          </DialogDescription>
        </DialogHeader>
        {conflict && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            You approved this payroll — for segregation of duties, a different officer must execute the payment.
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Payment date</Label>
            <Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} />
          </div>
          <div>
            <Label>Bank used</Label>
            <Select value={form.bank_account} onValueChange={(v) => setForm({ ...form, bank_account: v })}>
              <SelectTrigger data-testid="pay-bank-select"><SelectValue /></SelectTrigger>
              <SelectContent>{BANK_ACCOUNTS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Reference number *</Label>
            <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="pay-reference-input" />
          </div>
          <div className="col-span-2">
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} className="btn-primary-blue" disabled={busy || conflict} data-testid="pay-confirm-btn">
            {busy ? "Saving…" : "Confirm payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
